import { ChangeEvent, FormEvent, useState, FC } from 'react';
import { useTranslation } from 'react-i18next';
import { Button } from 'primereact/button';
import classNames from 'classnames';
import { Link } from 'react-router-dom';
import { InputTextField } from '@/components';
import { PagesPaths } from '@/enums';
import { useToastNotification } from '@/hooks';
import {
  validateEmail,
  validateName,
  validatePhone,
} from '@/utils/formValidations';
import { useMutation } from '@tanstack/react-query';
import { useGoogleReCaptcha } from 'react-google-recaptcha-v3';
import { registerUser } from '../../registerUser';
import styles from './SignUpForm.module.scss';

const initialFormData = {
  userName: '',
  fullName: '',
  email: '',
  phone: '',
};

type SignUpFormProps = {
  onSignUp: () => void;
};

const SignUpForm: FC<SignUpFormProps> = ({ onSignUp }) => {
  const { t } = useTranslation('signUp');
  const { showToast } = useToastNotification();

  const [formData, setFormData] = useState(initialFormData);
  const [formErrors, setFormErrors] = useState(initialFormData);

  const { executeRecaptcha } = useGoogleReCaptcha();

  const signUpMutation = useMutation({
    mutationFn: registerUser,
    onSuccess: () => onSignUp(),
    onError: () => {
      showToast({
        severity: 'error',
        detail: t('errors.toastText'),
      });
    },
  });

  const handleInputChange = (
    e: ChangeEvent<HTMLInputElement | HTMLTextAreaElement>,
    field: string
  ) => {
    const { value } = e.target;
    setFormData((prevData) => ({
      ...prevData,
      [field]: value,
    }));
    setFormErrors((prevErrors) => ({
      ...prevErrors,
      [field]: '',
    }));
  };

  const validateForm = () => {
    const userNameError = !formData.userName
      ? t('errors.userNameRequired')
      : '';
    const fullNameError = validateName(formData.fullName, {
      required: t('errors.nameRequired'),
      invalid: t('errors.invalidName'),
      lengthExceeded: t('errors.nameLengthExceeded'),
    });
    const phoneError = validatePhone(formData.phone, {
      required: t('errors.phoneRequired'),
      invalid: t('errors.invalidPhone'),
      lengthExceeded: t('errors.phoneLengthExceeded'),
    });
    const emailError = validateEmail(formData.email, {
      required: t('errors.emailRequired'),
      invalid: t('errors.invalidEmail'),
    });

    setFormErrors({
      userName: userNameError,
      fullName: fullNameError,
      email: emailError,
      phone: phoneError,
    });

    const isFormValid =
      !userNameError && !fullNameError && !phoneError && !emailError;

    return isFormValid;
  };

  const handleSubmit = async (e: FormEvent<HTMLFormElement>) => {
    e.preventDefault();

    if (!validateForm()) {
      return;
    }

    if (!executeRecaptcha) {
      showToast({
        severity: 'error',
        detail: t('errors.reCaptcha'),
      });
      return;
    }

    const reCaptchaToken = await executeRecaptcha();

    if (!reCaptchaToken) {
      showToast({
        severity: 'error',
        detail: t('errors.reCaptcha'),
      });
      return;
    }

    signUpMutation.mutate(formData);
  };

  const submitButtonText = signUpMutation.isPending
    ? t('buttonLoading')
    : t('buttonCreate');

  return (
    <div className={styles.signUpForm}>
      <h1>{t('pageTitle')}</h1>
      <form onSubmit={handleSubmit}>
        <InputTextField
          id="userName"
          name="userName"
          value={formData.userName}
          onChange={(e) => handleInputChange(e, e.target.name)}
          placeholder={t('usernamePlaceholder')}
          label={t('labelUsername')}
          error={formErrors.userName}
          help={t('usernameHelp')}
        />
        <InputTextField
          id="fullName"
          name="fullName"
          value={formData.fullName}
          onChange={(e) => handleInputChange(e, e.target.name)}
          placeholder={t('fullNamePlaceholder')}
          label={t('labelFullName')}
          error={formErrors.fullName}
        />
        <InputTextField
          id="email"
          name="email"
          type="email"
          value={formData.email}
          onChange={(e) => handleInputChange(e, e.target.name)}
          placeholder={t('emailPlaceholder')}
          label={t('labelEmail')}
          error={formErrors.email}
          help={t('emailHelp')}
        />
        <InputTextField
          id="phone"
          name="phone"
          type="tel"
          value={formData.phone}
          onChange={(e) => handleInputChange(e, e.target.name)}
          placeholder={t('phonePlaceholder')}
          label={t('labelPhone')}
          error={formErrors.phone}
          className={styles.lastInput}
        />
        <Button
          disabled={signUpMutation.isPending}
          type="submit"
          className={styles.button}
        >
          {submitButtonText}
        </Button>
        <Link
          to={PagesPaths.Login}
          className={classNames(styles.button, styles.secondaryButton)}
        >
          {t('buttonCancel')}
        </Link>
      </form>
    </div>
  );
};

export default SignUpForm;
